import { useStyletron } from '@thanx/uikit/theme'
import type { ReactNode } from 'react'

import type { PickerRow } from 'api/canonicalPicker.types'
import palette from 'constants/palette'
import { buildTranslate } from 'locales'

import locationNameOf from './locationNameOf'
import NoMatchBadge from './NoMatchBadge'

const t = buildTranslate('components.ai.picker.canonical')

const NAME_FONT_SIZE = '14px'
const NAME_LINE_HEIGHT = '20px'
const DETAIL_FONT_SIZE = '12px'
const DETAIL_LINE_HEIGHT = '16px'
const DETAIL_MARGIN_TOP = '6px'
const PILL_FONT_SIZE = '10.5px'
const UID_FONT_SIZE = '11.5px'
const PART_GAP = '8px'
// Space on each side of the dot between provider, UID and where it sells
const DETAIL_GAP = '6px'
// Between the words "Global UID" and the uid itself
const LABEL_GAP = '4px'

/**
 * Where a UID sells: its one location by name, or how many. Every location
 * nexus returns counts, even one the locations endpoint can't name. null for
 * a UID picked without its locations.
 */
export const locationLabelOf = (
  locationIds: number[] | null,
  locationNames: ReadonlyMap<number, string>,
  locationsFailed = false
): string | null => {
  if (locationIds === null) return null
  const ids = Array.from(new Set(locationIds))
  return ids.length === 1
    ? locationNameOf(ids[0], locationNames, locationsFailed)
    : t('locations_count', { count: ids.length })
}

type Props = {
  row: PickerRow
  locationNames?: ReadonlyMap<number, string>
  /** The locations list failed to load, so a location it would name is unavailable */
  locationsFailed?: boolean
  /** A store UID listed under its global, which already names the provider */
  isSubRow?: boolean
  /** What ends the row, like the control that expands a group */
  children?: ReactNode
}

const NO_NAMES: ReadonlyMap<number, string> = new Map()

/**
 * A picker option. A product shows its name. A UID group or store UID shows
 * its name, whole, over its provider as a small pill and its UID; a global
 * labels its UID "Global UID" and leaves where it sells to its store UIDs,
 * which say it at the end of their line, as a lone store UID does. A uid
 * added without a match shows itself, marked as matching nothing. Prices and
 * location names stay in the "What's inside" modal.
 */
const PickerRowLabel = ({
  row,
  locationNames = NO_NAMES,
  locationsFailed = false,
  isSubRow = false,
  children,
}: Props) => {
  const [css] = useStyletron()
  const separator = t('row_separator')

  // The option cuts its label to one line; this row wraps its name instead
  const nameClassName = css({
    fontSize: NAME_FONT_SIZE,
    lineHeight: NAME_LINE_HEIGHT,
    fontWeight: isSubRow ? 400 : 500,
    color: palette.grey90,
    whiteSpace: 'normal',
    overflowWrap: 'anywhere',
  })
  const detailClassName = css({
    display: 'flex',
    alignItems: 'center',
    gap: DETAIL_GAP,
    minWidth: 0,
    marginTop: DETAIL_MARGIN_TOP,
    fontSize: DETAIL_FONT_SIZE,
    lineHeight: DETAIL_LINE_HEIGHT,
    color: palette.grey70,
    whiteSpace: 'nowrap',
  })
  const uidClassName = css({
    fontFamily: 'monospace',
    fontSize: UID_FONT_SIZE,
    color: palette.grey90,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    minWidth: 0,
  })
  const pillClassName = css({
    flexShrink: 0,
    fontSize: PILL_FONT_SIZE,
    fontWeight: 600,
    letterSpacing: '.03em',
    padding: '1px 7px',
    borderRadius: '999px',
    backgroundColor: palette.grey10,
    color: palette.grey70,
  })
  const dotClassName = css({ flexShrink: 0, color: palette.grey40 })
  const dot = (
    <span aria-hidden="true" className={dotClassName}>
      {separator}
    </span>
  )

  if (row.kind === 'product') {
    return <span className={nameClassName}>{row.name}</span>
  }

  if (row.kind === 'unmatchedUid') {
    return (
      <span className={`d-flex align-items-center ${css({ gap: PART_GAP })}`}>
        <span className={uidClassName}>{row.uid}</span>
        <NoMatchBadge />
      </span>
    )
  }

  const isGlobal = row.kind === 'uidGroup' && row.isGlobal
  const name = row.kind === 'storeUid' ? row.storeName : row.name
  const uid = row.kind === 'storeUid' ? row.uid : row.uidGroup
  // A global's store UIDs say where they sell; the global itself doesn't
  const location = isGlobal
    ? null
    : locationLabelOf(row.locationIds, locationNames, locationsFailed)

  return (
    <span
      className={`d-flex align-items-start w-100 ${css({
        gap: PART_GAP,
        minWidth: 0,
      })}`}
    >
      <span className={css({ flex: '1 1 auto', minWidth: 0 })}>
        <span className={`d-block ${nameClassName}`}>{name}</span>
        <span className={detailClassName}>
          {!isSubRow && (
            <>
              <span className={pillClassName}>{row.provider}</span>
              {dot}
            </>
          )}
          <span
            className={css({
              display: 'flex',
              gap: LABEL_GAP,
              minWidth: 0,
            })}
          >
            {isGlobal && (
              <span className="text-nowrap">{t('global.uid_label')}</span>
            )}
            <span className={uidClassName} title={uid}>
              {uid}
            </span>
          </span>
          {location !== null && (
            <>
              {dot}
              <span className="text-nowrap">{location}</span>
            </>
          )}
        </span>
      </span>
      {children}
    </span>
  )
}

export default PickerRowLabel
